import type { BillingInterval, BillingQuote } from '@/services/billing'

/** "£49.00" in the bill's currency (amounts arrive as strings). */
export const formatMoney = (amount: number | string, currency: string) => {
  const value = Number(amount) || 0
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' }).format(value)
  } catch {
    return `${value.toFixed(2)} ${currency}`
  }
}

export const perInterval = (interval: BillingInterval) => (interval === 'year' ? 'a year' : 'a month')

/** "£60.00 a month" / "£648.00 a year". */
export const quoteTotal = (quote: BillingQuote) => `${formatMoney(quote.total, quote.currency)} ${perInterval(quote.interval)}`

/** Lines of a bill: the plan fee, the users it doesn't cover, then the top-up to the plan's minimum. */
export const quoteLines = (quote: BillingQuote) => {
  const lines: { label: string; amount: string }[] = []
  const money = (amount: number | string) => formatMoney(amount, quote.currency)
  if (Number(quote.base_amount) > 0) {
    lines.push({
      label: `${quote.plan_title} plan fee${quote.included_users ? ` (includes ${quote.included_users} users)` : ''}`,
      amount: money(quote.base_amount),
    })
  }
  if (quote.charged_admins) {
    lines.push({
      label: `${quote.charged_admins} Admin / Manager × ${money(quote.admin_amount)}`,
      amount: money(Number(quote.admin_amount) * quote.charged_admins),
    })
  }
  if (quote.charged_workers) {
    lines.push({
      label: `${quote.charged_workers} Field Worker × ${money(quote.worker_amount)}`,
      amount: money(Number(quote.worker_amount) * quote.charged_workers),
    })
  }
  if (Number(quote.topup) > 0) {
    lines.push({
      label: `Top-up to the ${quote.plan_title} ${money(quote.minimum_amount)} minimum`,
      amount: money(quote.topup),
    })
  }
  return lines
}
